import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { AudienceLevel, LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { SelectDropdown } from '../SelectDropdown';
import { ResetFiltersButton } from '../ResetFiltersButton';
import { ModuleCard } from './ModuleCard';
import { PathwayButton } from './PathwayButton';
import { TopicEditor } from './TopicEditor';
interface ResultsPanelProps {
  terms: string[];
  detectedTerms: string[];
  suggestions: string[];
  vocabulary: string[];
  results: MatchedModule[];
  pathwayIds: string[];
  audience: AudienceLevel | 'any';
  onToggleTerm: (term: string) => void;
  onToggleModule: (moduleId: string) => void;
  onReset: () => void;
}
const PAGE_SIZE = 8;
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-1729" data-mp="1exq2oxgvbhh1">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1730" data-mp="1eyhxw76jzd6s">
        {label}
      </span>
      {children}
    </div>;
}
export function ResultsPanel({
  terms,
  detectedTerms,
  suggestions,
  vocabulary,
  results,
  pathwayIds,
  audience,
  onToggleTerm,
  onToggleModule,
  onReset
}: ResultsPanelProps) {
  const [levelFilter, setLevelFilter] = useState<string>('any');
  const [categoryFilter, setCategoryFilter] = useState<string>('any');
  const [sort, setSort] = useState<string>('match');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [delta, setDelta] = useState<{
    term: string;
    count: number;
  } | null>(null);
  const pendingTerm = useRef<string | null>(null);
  const previousCount = useRef(results.length);
  const filtersActive = levelFilter !== 'any' || categoryFilter !== 'any' || sort !== 'match';
  const resetFilters = () => {
    setLevelFilter('any');
    setCategoryFilter('any');
    setSort('match');
    setPage(1);
  };

  // Editing topics re-ranks the list in place rather than dropping the rep back
  // into a loading state, so their scroll position and selections survive.
  const handleToggleTerm = (term: string) => {
    pendingTerm.current = term;
    previousCount.current = results.length;
    setPage(1);
    onToggleTerm(term);
  };
  useEffect(() => {
    if (!pendingTerm.current) return;
    setDelta({
      term: pendingTerm.current,
      count: results.length - previousCount.current
    });
    pendingTerm.current = null;
    const timer = window.setTimeout(() => setDelta(null), 4500);
    return () => window.clearTimeout(timer);
  }, [results]);
  const categories = useMemo(() => Array.from(new Set(results.map(r => r.module.category))).sort((a, b) => a.localeCompare(b)), [results]);
  const visible = useMemo(() => {
    let list = results.slice();
    if (levelFilter !== 'any') {
      list = list.filter(r => r.module.levels.includes(levelFilter as AudienceLevel));
    }
    if (categoryFilter !== 'any') {
      list = list.filter(r => r.module.category === categoryFilter);
    }
    if (sort === 'name') {
      list.sort((a, b) => a.module.name.localeCompare(b.module.name));
    } else {
      list.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    }
    return list;
  }, [results, levelFilter, categoryFilter, sort]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const resetPage = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };
  return <section aria-label="Matching skill modules" data-id="element-1731" data-mp="39i7bc954hej4">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3" data-id="element-1732" data-mp="39hed5wk8bxcl">
        <div data-id="element-1733" data-mp="asz2e78g0hx4">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1734" data-mp="atic50nxw002">
            {visible.length} matching {visible.length === 1 ? 'module' : 'modules'}
          </h2>
          <p className="mt-1 text-sm text-gray-600" data-id="element-1735" data-mp="3mo294h1wiyzu">
            {audience === 'any' ? 'Ranked by fit across all audiences.' : `Ranked for ${LEVEL_LABELS[audience].toLowerCase()}.`}
          </p>
        </div>
        <button type="button" onClick={onReset} className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors" data-id="element-1736" data-mp="3cryvadxotbx2">
          New search
        </button>
      </div>

      <div className="mb-5 rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-id="element-1737" data-mp="9bs2x3kq313l">
        <TopicEditor instanceId="results" heading="Topics in this search" activeTerms={terms} detectedTerms={detectedTerms} suggestedTerms={suggestions} suggestedLabel="Often paired with these" vocabulary={vocabulary} onToggleTerm={handleToggleTerm} emptyHint="Showing the full catalog." footer={<AnimatePresence initial={false} data-id="element-1739" data-mp="x0kt0jenyeqa">
              {delta && <motion.p key={`${delta.term}-${delta.count}`} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-3 text-xs font-medium text-[#30718D]" data-id="element-1740" data-mp="2hodrerop8nbu">
                  {delta.count > 0 ? `+${delta.count} modules from “${delta.term}”` : delta.count < 0 ? `${delta.count} modules after removing “${delta.term}”` : `No new modules from “${delta.term}”`}
                </motion.p>}
            </AnimatePresence>} data-id="element-1738" data-mp="e7yzi5r8b08v" />

        <div className="mt-4 grid items-end gap-3 border-t border-gray-100 pt-4 sm:grid-cols-[repeat(3,minmax(0,1fr))_auto]" data-id="element-1741" data-mp="3j6e75tn5umdl">
          <Field label="Audience level" data-id="element-1742" data-mp="3e8cr7hl1736c">
            <SelectDropdown ariaLabel="Filter by audience level" fullWidth scrollMenu value={levelFilter} onChange={resetPage(setLevelFilter)} options={[{
            value: 'any',
            label: 'All levels'
          }, ...(Object.keys(LEVEL_LABELS) as AudienceLevel[]).map(level => ({
            value: level,
            label: LEVEL_LABELS[level]
          }))]} data-id="element-1743" data-mp="3e9nsnar07yua" />
          </Field>
          <Field label="Category" data-id="element-1744" data-mp="1x626ixb0xjta">
            <SelectDropdown ariaLabel="Filter by category" fullWidth scrollMenu value={categoryFilter} onChange={resetPage(setCategoryFilter)} options={[{
            value: 'any',
            label: 'All categories'
          }, ...categories.map(category => ({
            value: category,
            label: category
          }))]} data-id="element-1745" data-mp="1x36a5detr36y" />
          </Field>
          <Field label="Sort by" data-id="element-1746" data-mp="16ugvkjjounv5">
            <SelectDropdown ariaLabel="Sort results" fullWidth value={sort} onChange={resetPage(setSort)} options={[{
            value: 'match',
            label: 'Best match'
          }, {
            value: 'name',
            label: 'Module name'
          }]} data-id="element-1747" data-mp="11wf1l3kq544w" />
          </Field>
          <ResetFiltersButton isActive={filtersActive} onReset={resetFilters} className="sm:mb-0.5" data-id="element-1748" data-mp="nxyw1fylljoc" />
        </div>
      </div>

      {pageItems.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white p-12 text-center shadow-sm" data-id="element-1749" data-mp="e6ui8jkns4v3">
          <SearchX size={28} className="mx-auto text-gray-400" data-id="element-1750" data-mp="992odk0ge8o9" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900" data-id="element-1751" data-mp="5664zr33a8j9">
            Nothing matches those filters
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600" data-id="element-1752" data-mp="7bmyrcy4o07">
            Try widening the audience level or removing a search term.
          </p>
          <PathwayButton variant="secondary" size="sm" className="mt-4" onClick={resetFilters} data-id="element-1753" data-mp="1t2ca8dre8hrd">
            Clear Filters
          </PathwayButton>
        </div> : <motion.div layout className="space-y-3" data-id="element-1754" data-mp="1j33k5vznfnaa">
          {pageItems.map(match => <motion.div key={match.module.id} layout initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1755" data-mp="15byrjdup7v9g">
              <ModuleCard match={match} inPathway={pathwayIds.includes(match.module.id)} expanded={expandedId === match.module.id} onToggleExpand={() => setExpandedId(expandedId === match.module.id ? null : match.module.id)} onTogglePathway={() => onToggleModule(match.module.id)} data-id="element-1756" data-mp="10dwxjxvqibj7" />
            </motion.div>)}
        </motion.div>}

      {pageCount > 1 && <nav aria-label="Results pages" className="mt-6 flex items-center justify-between" data-id="element-1757" data-mp="gt6fdc7yy11v">
          <PathwayButton variant="secondary" size="sm" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage === 1} data-id="element-1758" data-mp="coytrnwt517v">
            Previous
          </PathwayButton>
          <span className="text-sm text-gray-600" data-id="element-1759" data-mp="10g4eagdp0hoi">
            Page {safePage} of {pageCount}
          </span>
          <PathwayButton variant="secondary" size="sm" onClick={() => setPage(Math.min(pageCount, safePage + 1))} disabled={safePage === pageCount} data-id="element-1760" data-mp="vghshijstt43">
            Next
          </PathwayButton>
        </nav>}
    </section>;
}